// src/services/planejamentoAtendimentosApi.js
import {
  addDoc,
  collection,
  doc,
  deleteDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";

import { db } from "../config/firebase";

// Aplicado no modulo Planejamento (Atendimento/Visitas e Sessoes).
const COLLECTION_ATENDIMENTOS = "atendimentos_preventiva";
const COLLECTION_SESSOES = "atendimentos_sessoes";
const COLLECTION_SESSAO_ITENS = "atendimentos_sessao_itens";

function toTimestamp(value) {
  if (!value) return null;
  if (value instanceof Date) return Timestamp.fromDate(value);
  if (typeof value?.toDate === "function") return value;
  if (typeof value === "number") return Timestamp.fromMillis(value);
  return null;
}

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value === "number") return new Date(value);
  return null;
}

function tecnicoParticipaDaSessao(sessao, tecnicoId) {
  const tid = String(tecnicoId || "").trim();
  if (!tid) return true;
  const principal = String(sessao?.tecnicoId || "").trim() === tid;
  const acompanhantes = Array.isArray(sessao?.tecnicosAcompanhantesIds)
    ? sessao.tecnicosAcompanhantesIds.map((id) => String(id || "").trim())
    : [];
  return principal || acompanhantes.includes(tid);
}

function filtrarSessoesLocal(lista, inicioEm, fimEm, tecnicoId, empresaId) {
  const ini = toDate(inicioEm);
  const fim = toDate(fimEm);
  return (lista || []).filter((s) => {
    if (tecnicoId && !tecnicoParticipaDaSessao(s, tecnicoId)) return false;
    if (empresaId && String(s.empresaId || "") !== String(empresaId)) return false;
    const dt = toDate(s.inicioEm);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    if (ini && dt < ini) return false;
    if (fim && dt >= fim) return false;
    return true;
  });
}

function assertRequired(value, field) {
  if (value == null || value === "") {
    throw new Error(`${field} nao informado`);
  }
}

export async function criarAtendimento({
  grupoId,
  empresaId,
  clienteId,
  contratoTipo,
  competencia,
  progresso,
  setorId,
  academiaId,
}) {
  assertRequired(grupoId, "grupoId");
  assertRequired(empresaId, "empresaId");
  assertRequired(clienteId, "clienteId");
  assertRequired(contratoTipo, "contratoTipo");
  assertRequired(competencia, "competencia");

  const payload = {
    grupoId,
    empresaId,
    clienteId,
    contratoTipo,
    competencia,
    status: "ABERTO",
    startedAt: null,
    progresso: progresso || null,
    setorId: setorId || academiaId || null,
    academiaId: academiaId || setorId || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, COLLECTION_ATENDIMENTOS), payload);
  return { id: ref.id, ...payload };
}

export async function listarAtendimentosPorClienteCompetencia({
  grupoId,
  empresaId,
  clienteId,
  competencia,
}) {
  assertRequired(grupoId, "grupoId");

  const filters = [
    where("grupoId", "==", grupoId),
  ];

  if (empresaId) {
    filters.push(where("empresaId", "==", empresaId));
  }

  if (clienteId) {
    filters.push(where("clienteId", "==", clienteId));
  }

  if (competencia) {
    filters.push(where("competencia", "==", competencia));
  }

  const q = query(collection(db, COLLECTION_ATENDIMENTOS), ...filters);
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function criarSessao({
  grupoId,
  empresaId,
  atendimentoId,
  tecnicoId,
  inicioEm,
  fimEm,
  status,
  observacoes,
  financeiro,
}) {
  assertRequired(grupoId, "grupoId");
  assertRequired(empresaId, "empresaId");
  assertRequired(atendimentoId, "atendimentoId");

  const temAgendamento = !!tecnicoId && !!inicioEm;
  const statusFinal = status || (temAgendamento ? "AGENDADA" : "PLANEJADA");

  const payload = {
    grupoId,
    empresaId,
    atendimentoId,
    tecnicoId: tecnicoId || null,
    inicioEm: toTimestamp(inicioEm),
    fimEm: toTimestamp(fimEm),
    status: statusFinal,
    observacoes: observacoes || null,
    kmRodados: financeiro?.kmRodados ?? null,
    valorDeslocamento: financeiro?.valorDeslocamento ?? null,
    valorPedagio: financeiro?.valorPedagio ?? null,
    valorMaoObra: financeiro?.valorMaoObra ?? null,
    valorPecas: financeiro?.valorPecas ?? null,
    valorDesconto: financeiro?.valorDesconto ?? null,
    valorTotal: financeiro?.valorTotal ?? null,
    enderecoDestino: financeiro?.enderecoDestino ?? null,
    destinoLat: financeiro?.destinoLat ?? null,
    destinoLng: financeiro?.destinoLng ?? null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, COLLECTION_SESSOES), payload);
  return { id: ref.id, ...payload };
}

export async function listarSessoesDoAtendimento({
  grupoId,
  empresaId,
  atendimentoId,
}) {
  assertRequired(grupoId, "grupoId");
  assertRequired(empresaId, "empresaId");
  assertRequired(atendimentoId, "atendimentoId");

  const q = query(
    collection(db, COLLECTION_SESSOES),
    where("grupoId", "==", grupoId),
    where("empresaId", "==", empresaId),
    where("atendimentoId", "==", atendimentoId)
  );

  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listarSessoesPorTecnicoPeriodo({
  grupoId,
  empresaId,
  tecnicoId,
  inicioEm,
  fimEm,
}) {
  assertRequired(grupoId, "grupoId");
  assertRequired(tecnicoId, "tecnicoId");
  assertRequired(inicioEm, "inicioEm");
  assertRequired(fimEm, "fimEm");

  const filtrosPrincipal = [
    where("grupoId", "==", grupoId),
    where("tecnicoId", "==", tecnicoId),
    where("inicioEm", ">=", toTimestamp(inicioEm)),
    where("inicioEm", "<", toTimestamp(fimEm)),
  ];

  const filtrosAcompanhante = [
    where("grupoId", "==", grupoId),
    where("tecnicosAcompanhantesIds", "array-contains", tecnicoId),
    where("inicioEm", ">=", toTimestamp(inicioEm)),
    where("inicioEm", "<", toTimestamp(fimEm)),
  ];

  if (empresaId) {
    filtrosPrincipal.push(where("empresaId", "==", empresaId));
    filtrosAcompanhante.push(where("empresaId", "==", empresaId));
  }

  try {
    const [snapPrincipal, snapAcompanhante] = await Promise.all([
      getDocs(query(collection(db, COLLECTION_SESSOES), ...filtrosPrincipal)),
      getDocs(query(collection(db, COLLECTION_SESSOES), ...filtrosAcompanhante)),
    ]);
    const map = new Map();
    [...snapPrincipal.docs, ...snapAcompanhante.docs].forEach((d) => {
      map.set(d.id, { id: d.id, ...d.data() });
    });
    return Array.from(map.values());
  } catch (e) {
    console.error("Erro ao listar sessoes por tecnico (fallback):", e);
    const snap = await getDocs(
      query(
        collection(db, COLLECTION_SESSOES),
        where("grupoId", "==", grupoId),
        limit(2000)
      )
    );
    const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return filtrarSessoesLocal(lista, inicioEm, fimEm, tecnicoId, empresaId);
  }
}

export async function listarSessoesPorPeriodo({
  grupoId,
  empresaId,
  tecnicoId,
  inicioEm,
  fimEm,
}) {
  assertRequired(grupoId, "grupoId");
  assertRequired(inicioEm, "inicioEm");
  assertRequired(fimEm, "fimEm");

  const filtrosBase = [
    where("grupoId", "==", grupoId),
    where("inicioEm", ">=", toTimestamp(inicioEm)),
    where("inicioEm", "<", toTimestamp(fimEm)),
  ];

  if (empresaId) {
    filtrosBase.push(where("empresaId", "==", empresaId));
  }

  try {
    if (tecnicoId) {
      const filtrosPrincipal = [...filtrosBase, where("tecnicoId", "==", tecnicoId)];
      const filtrosAcompanhante = [...filtrosBase, where("tecnicosAcompanhantesIds", "array-contains", tecnicoId)];
      const [snapPrincipal, snapAcompanhante] = await Promise.all([
        getDocs(query(collection(db, COLLECTION_SESSOES), ...filtrosPrincipal)),
        getDocs(query(collection(db, COLLECTION_SESSOES), ...filtrosAcompanhante)),
      ]);
      const map = new Map();
      [...snapPrincipal.docs, ...snapAcompanhante.docs].forEach((d) => {
        map.set(d.id, { id: d.id, ...d.data() });
      });
      return Array.from(map.values());
    }

    const q = query(collection(db, COLLECTION_SESSOES), ...filtrosBase);
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.error("Erro ao listar sessoes por periodo (fallback):", e);
    const snap = await getDocs(
      query(
        collection(db, COLLECTION_SESSOES),
        where("grupoId", "==", grupoId),
        limit(2000)
      )
    );
    const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return filtrarSessoesLocal(lista, inicioEm, fimEm, tecnicoId, empresaId);
  }
}

export async function atualizarSessao({
  sessaoId,
  inicioEm,
  fimEm,
  status,
  observacoes,
  tecnicoId,
  financeiro,
}) {
  assertRequired(sessaoId, "sessaoId");
  assertRequired(inicioEm, "inicioEm");

  const payload = {
    inicioEm: toTimestamp(inicioEm),
    fimEm: toTimestamp(fimEm),
    status: status || "PLANEJADA",
    observacoes: observacoes || null,
    kmRodados: financeiro?.kmRodados ?? null,
    valorDeslocamento: financeiro?.valorDeslocamento ?? null,
    valorPedagio: financeiro?.valorPedagio ?? null,
    valorMaoObra: financeiro?.valorMaoObra ?? null,
    valorPecas: financeiro?.valorPecas ?? null,
    valorDesconto: financeiro?.valorDesconto ?? null,
    valorTotal: financeiro?.valorTotal ?? null,
    enderecoDestino: financeiro?.enderecoDestino ?? null,
    destinoLat: financeiro?.destinoLat ?? null,
    destinoLng: financeiro?.destinoLng ?? null,
    updatedAt: serverTimestamp(),
  };

  if (tecnicoId !== undefined) {
    payload.tecnicoId = tecnicoId || null;
  }

  const ref = doc(db, COLLECTION_SESSOES, sessaoId);
  await updateDoc(ref, payload);
  return { id: sessaoId, ...payload };
}

export async function listarItensSessao({
  grupoId,
  empresaId,
  sessaoId,
}) {
  assertRequired(grupoId, "grupoId");
  assertRequired(empresaId, "empresaId");
  assertRequired(sessaoId, "sessaoId");

  const baseFilters = [
    where("grupoId", "==", grupoId),
    where("sessaoId", "==", sessaoId),
  ];

  const snap = await getDocs(
    query(
      collection(db, COLLECTION_SESSAO_ITENS),
      ...baseFilters,
      where("empresaId", "==", empresaId)
    )
  );

  if (snap.size > 0) {
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  const fallbackSnap = await getDocs(
    query(collection(db, COLLECTION_SESSAO_ITENS), ...baseFilters)
  );

  return fallbackSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((item) => !item.empresaId || String(item.empresaId) === String(empresaId));
}

export async function listarItensSessaoResumo({
  grupoId,
  empresaId,
  sessaoId,
  limite = 10,
}) {
  assertRequired(grupoId, "grupoId");
  assertRequired(empresaId, "empresaId");
  assertRequired(sessaoId, "sessaoId");

  const baseFilters = [
    where("grupoId", "==", grupoId),
    where("sessaoId", "==", sessaoId),
  ];

  const snap = await getDocs(
    query(
      collection(db, COLLECTION_SESSAO_ITENS),
      ...baseFilters,
      where("empresaId", "==", empresaId),
      limit(limite)
    )
  );

  if (snap.size > 0) {
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  const fallbackSnap = await getDocs(
    query(collection(db, COLLECTION_SESSAO_ITENS), ...baseFilters)
  );

  return fallbackSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((item) => !item.empresaId || String(item.empresaId) === String(empresaId))
    .slice(0, limite);
}

export async function contarItensSessao({
  grupoId,
  empresaId,
  sessaoId,
}) {
  assertRequired(grupoId, "grupoId");
  assertRequired(empresaId, "empresaId");
  assertRequired(sessaoId, "sessaoId");

  const baseFilters = [
    where("grupoId", "==", grupoId),
    where("sessaoId", "==", sessaoId),
  ];

  const snap = await getDocs(
    query(
      collection(db, COLLECTION_SESSAO_ITENS),
      ...baseFilters,
      where("empresaId", "==", empresaId)
    )
  );

  if (snap.size > 0) {
    return snap.size;
  }

  const fallbackSnap = await getDocs(
    query(collection(db, COLLECTION_SESSAO_ITENS), ...baseFilters)
  );

  return fallbackSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((item) => !item.empresaId || String(item.empresaId) === String(empresaId))
    .length;
}

export async function adicionarItensSessao({
  grupoId,
  empresaId,
  atendimentoId,
  sessaoId,
  clienteId,
  itens,
}) {
  assertRequired(grupoId, "grupoId");
  assertRequired(empresaId, "empresaId");
  assertRequired(atendimentoId, "atendimentoId");
  assertRequired(sessaoId, "sessaoId");
  assertRequired(clienteId, "clienteId");

  if (!Array.isArray(itens) || itens.length === 0) return [];

  const batch = writeBatch(db);
  const created = [];

  itens.forEach((item) => {
    const rawTipo = String(item?.tipo || "").toLowerCase().trim();
    const isOs = rawTipo === "os" || !!item?.osId;
    const isPreventiva = rawTipo === "preventiva" || rawTipo === "prev" || !!item?.preventivaId;
    const isInstalacao = rawTipo === "instalacao" || rawTipo === "instalação" || !!item?.propostaInstalacaoId;

    const osId = isOs ? (item?.osId || item?.id) : null;
    const preventivaId = isPreventiva ? (item?.preventivaId || item?.id) : null;
    const propostaInstalacaoId = item?.propostaInstalacaoId || null;
    const equipamentoId = (!isOs && !isPreventiva)
      ? (item?.equipamentoId || propostaInstalacaoId || item?.id)
      : null;

    // Se osId começa com "OS:" é porque está usando item.id como fallback
    // Nesse caso, extrai apenas o ID sem o prefixo
    const osIdFinal = osId && String(osId).startsWith("OS:") ? String(osId).replace(/^OS:/, "") : osId;

    if (!equipamentoId && !osIdFinal && !preventivaId) return;

    const ref = doc(collection(db, COLLECTION_SESSAO_ITENS));
    const payload = {
      grupoId,
      empresaId,
      atendimentoId,
      sessaoId,
      clienteId,
      tipo: isOs ? "OS" : (isPreventiva ? "PREVENTIVA" : (isInstalacao ? "INSTALACAO" : "EQUIPAMENTO")),
      equipamentoId: equipamentoId || null,
      equipamentoNome: !isOs ? (item?.equipamentoNome || item?.nome || null) : null,
      preventivaModeloId: !isOs ? (item?.preventivaModeloId || null) : null,
      checklistInstalacaoId: isInstalacao ? (item?.checklistInstalacaoId || null) : null,
      checklistInstalacaoNome: isInstalacao ? (item?.checklistInstalacaoNome || null) : null,
      propostaInstalacaoId: propostaInstalacaoId || null,
      propostaInstalacaoNome: item?.propostaInstalacaoNome || null,

      // Preventiva (instância)
      preventivaId: isPreventiva ? (preventivaId || null) : null,
      preventivaCodigo: isPreventiva ? (item?.preventivaCodigo || item?.codigo || null) : null,
      preventivaNome: isPreventiva ? (item?.preventivaNome || item?.nome || null) : null,
      maquinaId: isPreventiva ? (item?.maquinaId || null) : null,
      maquinaNome: isPreventiva ? (item?.maquinaNome || null) : null,
      periodicidade: isPreventiva ? (item?.periodicidade || null) : null,
      camadaPrevista: isPreventiva ? (item?.camadaPrevista || null) : null,

      // OS (corretivas / outras)
      osId: osIdFinal || null,
      osCodigo: isOs ? (item?.osCodigo || item?.codigo || null) : null,
      osTitulo: isOs ? (item?.osTitulo || item?.titulo || item?.descricao || null) : null,
      osTipoServico: isOs ? (item?.osTipoServico || item?.tipoServico || null) : null,

      // Campos em comum
      setorId: item?.setorId || null,
      setorNome: item?.setorNome || null,

      // Competência / slot (para não "sumir" em outros meses/quinzenas)
      competencia: item?.competencia || null,
      competenciaMes:
        item?.competenciaMes ||
        (item?.competencia ? String(item.competencia).slice(0, 7) : null),
      quinzena: item?.quinzena || null,
      slotKey:
        item?.slotKey ||
        (isPreventiva && (item?.competencia || null) && (preventivaId || null)
          ? `${preventivaId}|${item.competencia}`
          : null),

      createdAt: serverTimestamp(),
    };

    batch.set(ref, payload);
    created.push({ id: ref.id, ...payload });
  });

  await batch.commit();
  return created;
}

export async function removerItensSessao({
  itensIds,
}) {
  if (!Array.isArray(itensIds) || itensIds.length === 0) return;

  const batch = writeBatch(db);
  itensIds.forEach((id) => {
    if (!id) return;
    batch.delete(doc(db, COLLECTION_SESSAO_ITENS, id));
  });

  await batch.commit();
}

async function deleteDocsInBatches(refs) {
  const chunks = [];
  const batchSize = 400;
  for (let i = 0; i < refs.length; i += batchSize) {
    chunks.push(refs.slice(i, i + batchSize));
  }
  for (const chunk of chunks) {
    const batch = writeBatch(db);
    chunk.forEach((ref) => batch.delete(ref));
    await batch.commit();
  }
}

export async function excluirAtendimento({
  grupoId,
  empresaId,
  atendimentoId,
}) {
  assertRequired(grupoId, "grupoId");
  assertRequired(atendimentoId, "atendimentoId");

  const sessaoFilters = [
    where("grupoId", "==", grupoId),
    where("atendimentoId", "==", atendimentoId),
  ];
  if (empresaId) {
    sessaoFilters.push(where("empresaId", "==", empresaId));
  }

  const sessaoSnap = await getDocs(query(collection(db, COLLECTION_SESSOES), ...sessaoFilters));
  const sessoes = sessaoSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const itensRefs = [];
  for (const sessao of sessoes) {
    const itensFilters = [
      where("grupoId", "==", grupoId),
      where("sessaoId", "==", sessao.id),
    ];
    if (empresaId) {
      itensFilters.push(where("empresaId", "==", empresaId));
    }
    const itensSnap = await getDocs(query(collection(db, COLLECTION_SESSAO_ITENS), ...itensFilters));
    itensSnap.docs.forEach((d) => itensRefs.push(d.ref));
  }

  if (itensRefs.length) {
    await deleteDocsInBatches(itensRefs);
  }

  if (sessaoSnap.docs.length) {
    await deleteDocsInBatches(sessaoSnap.docs.map((d) => d.ref));
  }

  await deleteDoc(doc(db, COLLECTION_ATENDIMENTOS, atendimentoId));
}

export async function excluirSessao({
  grupoId,
  empresaId,
  sessaoId,
}) {
  assertRequired(grupoId, "grupoId");
  assertRequired(sessaoId, "sessaoId");

  const itensFilters = [
    where("grupoId", "==", grupoId),
    where("sessaoId", "==", sessaoId),
  ];
  if (empresaId) {
    itensFilters.push(where("empresaId", "==", empresaId));
  }

  const itensSnap = await getDocs(query(collection(db, COLLECTION_SESSAO_ITENS), ...itensFilters));
  if (itensSnap.docs.length) {
    await deleteDocsInBatches(itensSnap.docs.map((d) => d.ref));
  }

  await deleteDoc(doc(db, COLLECTION_SESSOES, sessaoId));
}
